import { PackageIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Badge } from '@/components/ui/badge'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Typography } from '@/components/ui/typography'
import { DEFAULT_TZ, EMPTY_VALUE } from '@/constants'
import { PackageModel } from '@/models'
import { getPackageBadgeColor, getPackageStatusLabels } from '@/pages/packages'
import { CARRIER_LOGO_MAP } from '@/pages/packages/package-duplicate-modal/package-duplicate-modal.constants'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { formatDateOnly, formatTimeOnly, getCarrierName } from '@/utils'

import * as S from './package-duplicate-modal.styled'

interface PackageDuplicateModalProps {
  duplicatePackage: PackageModel
  onClose: () => void
  onSaveAsNew: () => void
}

export const PackageDuplicateModal = ({
  duplicatePackage,
  onClose,
  onSaveAsNew,
}: PackageDuplicateModalProps) => {
  const { t } = useTranslation(['packages'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const timezone = activeBuilding?.timezone ?? DEFAULT_TZ

  const carrierLogo = CARRIER_LOGO_MAP[duplicatePackage.carrier]

  const createdAt = duplicatePackage.checkinAt
  const formattedDate = createdAt
    ? `${formatDateOnly(toZonedTime(createdAt, timezone))} ${formatTimeOnly(toZonedTime(createdAt, timezone))}`
    : EMPTY_VALUE

  return (
    <ConfirmationPopup
      autoWidth
      title={t('packages:packageDuplicateModal.title')}
      variant="warning"
      confirmText={t('packages:packageDuplicateModal.confirm')}
      cancelText={t('packages:packageDuplicateModal.cancel')}
      headerIcon={PackageIcon}
      onClose={onClose}
      onConfirm={onSaveAsNew}
    >
      <S.Content>
        <Typography weight="medium" size="heading-medium" color="black700">
          {t('packages:packageDuplicateModal.description')}
        </Typography>
        <S.DetailsCard>
          <S.DetailRow>
            <Typography weight="medium" as="span" size="text-medium" color="black800">
              {t('packages:packageDuplicateModal.carrierLabel')}
            </Typography>
            {carrierLogo ? (
              <S.CarrierLogo src={carrierLogo} alt={getCarrierName(duplicatePackage.carrier)} />
            ) : (
              <Typography as="span" size="text-medium" color="black700" weight="medium">
                {getCarrierName(duplicatePackage.carrier)}
              </Typography>
            )}
          </S.DetailRow>
          <S.Separator />
          <S.DetailRow>
            <Typography weight="medium" as="span" size="text-medium" color="black800">
              {t('packages:packageDuplicateModal.trackingLabel')}
            </Typography>
            <Typography as="span" size="text-medium" color="black700" weight="regular">
              {duplicatePackage.trackingNumber}
            </Typography>
          </S.DetailRow>
          <S.Separator />
          <S.DetailRow>
            <Typography weight="medium" as="span" size="text-medium" color="black800">
              {t('packages:packageDuplicateModal.recipientLabel')}
            </Typography>
            <Typography as="span" size="text-medium" color="black700" weight="regular">
              {duplicatePackage.residentName
                ? t('packages:packageDuplicateModal.recipientValue', {
                    name: duplicatePackage.residentName,
                    unit: duplicatePackage.residentUnit,
                  })
                : EMPTY_VALUE}
            </Typography>
          </S.DetailRow>
          <S.Separator />
          <S.DetailRow>
            <Typography weight="medium" as="span" size="text-medium" color="black800">
              {t('packages:packageDuplicateModal.createdLabel')}
            </Typography>
            <Typography as="span" size="text-medium" color="black700" weight="regular">
              {formattedDate}
            </Typography>
          </S.DetailRow>
          <S.Separator />
          <S.DetailRow>
            <Typography weight="medium" as="span" size="text-medium" color="black800">
              {t('packages:packageDuplicateModal.statusLabel')}
            </Typography>
            <Badge
              color={getPackageBadgeColor(duplicatePackage.status)}
              variant="light"
              size="small"
            >
              {getPackageStatusLabels(t)[duplicatePackage.status]}
            </Badge>
          </S.DetailRow>
        </S.DetailsCard>

        <Typography weight="medium" size="text-large" color="black700">
          {t('packages:packageDuplicateModal.footer')}
        </Typography>
      </S.Content>
    </ConfirmationPopup>
  )
}
